/**
 * Firebase Storage Service
 * Handles file uploads, downloads, and deletions for media files
 */

import { storage } from '../config/firebase';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject, 
  getMetadata,
  listAll,
  updateMetadata,
  StorageReference,
  UploadResult as FirebaseUploadResult,
  FullMetadata
} from 'firebase/storage';
import toast from 'react-hot-toast';

// Media types
type MediaType = 'image' | 'audio' | 'document' | 'video';

interface MediaFile {
  name: string;
  size: number;
  type: string;
  lastModified: number;
}

interface UploadOptions {
  customName?: string;
  userId?: string;
  metadata?: Record<string, string>;
  customNames?: string[];
}

interface UploadResult {
  success: boolean;
  url: string;
  path: string;
  metadata: {
    name: string;
    size: number;
    contentType: string;
    timeCreated: string;
    customMetadata: Record<string, string>;
  };
}

interface MediaValidation {
  isValid: boolean;
  errors: string[];
}

class MediaService {
  private storage: typeof storage;
  private allowedTypes: Record<MediaType, string[]>;
  private maxFileSizes: Record<MediaType, number>;

  constructor() {
    this.storage = storage;
    this.allowedTypes = {
      image: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
      audio: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a'],
      document: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      video: ['video/mp4', 'video/webm', 'video/ogg', 'video/avi']
    };
    this.maxFileSizes = {
      image: 5 * 1024 * 1024, // 5MB
      audio: 50 * 1024 * 1024, // 50MB
      document: 20 * 1024 * 1024, // 20MB
      video: 100 * 1024 * 1024 // 100MB
    };
  }

  /**
   * Validate file before upload
   * @param file - File to validate
   * @param type - File type (image, audio, document, video)
   * @returns Validation result
   */
  validateFile(file: File, type: MediaType): MediaValidation {
    const errors = [];

    // Check if file exists
    if (!file) {
      errors.push('No file selected');
      return { isValid: false, errors };
    }

    // Check file type
    if (!this.allowedTypes[type]?.includes(file.type)) {
      errors.push(`Invalid file type. Allowed types: ${this.allowedTypes[type].join(', ')}`);
    }

    // Check file size
    if (file.size > this.maxFileSizes[type]) {
      const maxSizeMB = this.maxFileSizes[type] / (1024 * 1024);
      errors.push(`File size too large. Maximum size: ${maxSizeMB}MB`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate unique filename with timestamp
   * @param originalName - Original filename
   * @param type - File type
   * @returns Unique filename
   */
  generateFileName(originalName: string, type: MediaType): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop();
    return `${type}/${timestamp}_${randomString}.${extension}`;
  }

  /**
   * Upload file to Firebase Storage
   * @param file - File to upload
   * @param type - File type (image, audio, document, video)
   * @param options - Upload options
   * @returns Upload result with URL and metadata
   */
  async uploadFile(file: File, type: MediaType, options: UploadOptions = {}): Promise<UploadResult> {
    try {
      // Validate file
      const validation = this.validateFile(file, type);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Generate filename
      const fileName = options.customName || this.generateFileName(file.name, type);
      const storageRef: StorageReference = ref(this.storage, fileName);

      // Prepare metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          originalName: file.name,
          uploadedAt: new Date().toISOString(),
          uploadedBy: options.userId || 'anonymous',
          type: type,
          ...options.metadata
        }
      };

      // Upload file
      const uploadTask: FirebaseUploadResult = await uploadBytes(storageRef, file, metadata);
      
      // Get download URL
      const downloadURL: string = await getDownloadURL(uploadTask.ref);

      // Get file metadata
      const fileMetadata: FullMetadata = await getMetadata(uploadTask.ref);

      const result: UploadResult = {
        success: true,
        url: downloadURL,
        path: fileName,
        metadata: {
          name: fileMetadata.name,
          size: fileMetadata.size,
          contentType: fileMetadata.contentType || '',
          timeCreated: fileMetadata.timeCreated || new Date().toISOString(),
          customMetadata: fileMetadata.customMetadata || {}
        }
      };

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully`);
      return result;

    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Upload multiple files
   * @param {File[]} files - Array of files to upload
   * @param {string} type - File type
   * @param {Object} options - Upload options
   * @returns {Promise<Object[]>} Array of upload results
   */
  async uploadMultipleFiles(files: File[], type: MediaType, options: UploadOptions = {}): Promise<UploadResult[]> {
    const uploadPromises = files.map((file, index) => 
      this.uploadFile(file, type, {
        ...options,
        customName: options.customNames?.[index]
      })
    );

    try {
      const results = await Promise.all(uploadPromises);
      toast.success(`${files.length} files uploaded successfully`);
      return results;
    } catch (error) {
      console.error('Multiple upload error:', error);
      toast.error('Some files failed to upload');
      throw error;
    }
  }

  /**
   * Delete file from Firebase Storage
   * @param {string} filePath - Path to file in storage
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFile(filePath: string): Promise<{ success: boolean; message?: string }> {
    try {
      const fileRef = ref(this.storage, filePath);
      await deleteObject(fileRef);

      const result = {
        success: true,
        path: filePath,
        deletedAt: new Date().toISOString()
      };

      toast.success('File deleted successfully');
      return result;

    } catch (error: any) {
      console.error('Delete error:', error);
      if (error.code === 'storage/object-not-found') {
        toast.error('File not found');
      } else {
        toast.error(`Delete failed: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Delete multiple files
   * @param {string[]} filePaths - Array of file paths
   * @returns {Promise<Object[]>} Array of deletion results
   */
  async deleteMultipleFiles(filePaths: string[]): Promise<{ success: boolean; message?: string }[]> {
    const deletePromises = filePaths.map((path: string) => this.deleteFile(path));

    try {
      const results = await Promise.all(deletePromises);
      toast.success(`${filePaths.length} files deleted successfully`);
      return results;
    } catch (error) {
      console.error('Multiple delete error:', error);
      toast.error('Some files failed to delete');
      throw error;
    }
  }

  /**
   * Get file metadata
   * @param {string} filePath - Path to file
   * @returns {Promise<Object>} File metadata
   */
  async getFileMetadata(filePath: string): Promise<FullMetadata | null> {
    try {
      const fileRef = ref(this.storage, filePath);
      const metadata = await getMetadata(fileRef);
      
      return metadata;
    } catch (error) {
      console.error('Get metadata error:', error);
      throw error;
    }
  }

  /**
   * List files in a folder
   * @param {string} folderPath - Path to folder
   * @returns {Promise<Object[]>} Array of file objects
   */
  async listFiles(folderPath: string): Promise<{ files: string[]; folders: string[] }> {
    try {
      const folderRef = ref(this.storage, folderPath);
      const result = await listAll(folderRef);
      
      const files = await Promise.all(
        result.items.map(async (item) => {
          const metadata = await getMetadata(item);
          const downloadURL = await getDownloadURL(item);
          
          return {
            name: metadata.name,
            path: item.fullPath,
            url: downloadURL,
            size: metadata.size,
            contentType: metadata.contentType,
            timeCreated: metadata.timeCreated,
            customMetadata: metadata.customMetadata
          };
        })
      );

      return {
        files: files.map(f => f.path),
        folders: result.prefixes.map(p => p.name)
      };
    } catch (error) {
      console.error('List files error:', error);
      throw error;
    }
  }

  /**
   * Update file metadata
   * @param {string} filePath - Path to file
   * @param {Object} newMetadata - New metadata to set
   * @returns {Promise<Object>} Update result
   */
  async updateFileMetadata(filePath: string, newMetadata: Record<string, string>): Promise<void> {
    try {
      const fileRef = ref(this.storage, filePath);
      await updateMetadata(fileRef, {
        customMetadata: newMetadata
      });

      toast.success('File metadata updated successfully');
    } catch (error: any) {
      console.error('Update metadata error:', error);
      toast.error(`Update failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get file download URL
   * @param {string} filePath - Path to file
   * @returns {Promise<string>} Download URL
   */
  async getDownloadURL(filePath: string): Promise<string> {
    try {
      const fileRef = ref(this.storage, filePath);
      return await getDownloadURL(fileRef);
    } catch (error: any) {
      console.error('Get download URL error:', error);
      throw error;
    }
  }

  /**
   * Get storage usage statistics
   * @returns {Promise<Object>} Storage statistics
   */
  async getStorageStats(): Promise<{ success: boolean; stats: { totalSize: number; fileCounts: Record<string, number> } }> {
    try {
      const rootRef = ref(this.storage);
      const result = await listAll(rootRef);
      
      let totalSize = 0;
      const fileCounts: Record<string, number> = {
        image: 0,
        audio: 0,
        document: 0,
        video: 0,
        other: 0
      };

      for (const item of result.items) {
        const metadata = await getMetadata(item);
        totalSize += parseInt(String(metadata.size || 0));
        
        const type = metadata.customMetadata?.type || 'other';
        if (type in fileCounts) {
          fileCounts[type]++;
        } else {
          fileCounts.other++;
        }
      }

      return {
        success: true,
        stats: {
          totalSize,
          fileCounts
        }
      };
    } catch (error) {
      console.error('Get storage stats error:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const mediaService = new MediaService();
export default mediaService;
